import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AgendaAlertaTurno,
  AgendaBloqueio,
  AgendaClassificacao,
  AgendaContexto,
  AgendaGradeLinha,
  AgendaPaginacao,
  AgendaResumoDia,
} from '../../../../interfaces/agenda';
import { AgendaModaisService } from '../../../../services/agenda/agenda-modais.service';
import { AgendaService, mensagemErroAgenda } from '../../../../services/agenda/agenda.service';
import { AgendaAcaoAgendamento, motivoAcaoSelecao } from '../agenda-acoes';
import { exibicaoConvenio } from '../agenda-agendamento/agenda-agendamento';
import { AgendaFiltroAplicado, paramsGrade, somarDias } from '../agenda-filtro';
import {
  AgendaColunaDef,
  AgendaColunaPref,
  alternarColuna,
  colunasPadrao,
  colunasVisiveis,
  definirMedico,
  itensSeletor,
  mesclarColunas,
  moverColuna,
  moverColunaPara,
  restaurarColunas,
} from './agenda-colunas';
import {
  AgendaChipResumo,
  AgendaOrdenacao,
  LIMITE_PAGINA,
  LegendaClassificacao,
  ORDENACAO_PADRAO,
  STATUS_LEGENDA,
  aceitaAgendamento,
  bloqueioTotal,
  chaveTurno,
  chipsResumo,
  classificacoesDoTipo,
  classeEspera,
  classeStatus,
  corClassificacao,
  descreverBloqueio,
  ehBloqueado,
  ehCancelado,
  ehLivre,
  ehMarcado,
  esperaPrincipal,
  esperaSub,
  formatarCpf,
  formatarEspera,
  legendaClassificacoes,
  selecionavel,
  subAgendadoPor,
  subPaciente,
  temPaciente,
} from './agenda-grade';

export type AgendaAcaoGrade = 'reservar' | 'liberarReserva' | AgendaAcaoAgendamento;

export interface AgendaAcaoGradeEvento {
  acao: AgendaAcaoGrade;
  linhas: AgendaGradeLinha[];
}

export interface AgendaMenuLinhaEvento {
  linha: AgendaGradeLinha;
  /** Botão ⋮ clicado, para posicionar o menu embaixo dele. */
  alvo?: HTMLElement;
  /** Clique direito: abre na posição do cursor. */
  ponto?: { x: number; y: number };
  /** Seleção de agendados no momento do clique (⋮ e botão direito igualam a seleção antes de emitir). */
  marcadas?: AgendaGradeLinha[];
  /** Mesma regra do botão Encaixe da barra — calculada uma vez só, aqui apenas se repassa. */
  podeEncaixe?: boolean;
  motivoEncaixe?: string;
}

interface OpcoesCarga {
  /** false = troca o dado sem mostrar o esqueleto. */
  piscar: boolean;
  manterSelecao: boolean;
  /** false = erro não troca a grade boa da tela por mensagem (atualização automática). */
  mostrarErro: boolean;
}

export const ESPERA_SALVAR_COLUNAS_MS = 800;
export const DURACAO_ERRO_COLUNAS_MS = 6000;
const DIAS_AVISO_TURNO = 30;

// Larguras fixas no SCSS (.col-chk, .c-hora, .c-tipo): o left das colunas presas soma estas.
const LARGURA_CHK = 34;
const LARGURA_FIXA: Record<string, number | undefined> = { hora: 104, tipo: 78 };
const FIXAVEIS = new Set(['hora', 'tipo', 'paciente']);

// Paciente movido para o fim da grade não deve prender e cobrir as outras na rolagem.
const ALCANCE_FIXAS = 5;

/**
 * Hora, Tipo e Paciente entre as primeiras colunas ficam presas; as do meio passam por baixo
 * delas. Paciente tem largura livre, então nada depois dele prende.
 */
export function colunasFixas(lista: readonly AgendaColunaDef[]): Map<string, number> {
  const m = new Map<string, number>();
  let left = LARGURA_CHK;
  for (const c of lista.slice(0, ALCANCE_FIXAS)) {
    if (!FIXAVEIS.has(c.key)) continue;
    m.set(c.key, left);
    const w = LARGURA_FIXA[c.key];
    if (w === undefined) break;
    left += w;
  }
  return m;
}

/**
 * Grade do dia. Recebe o filtro aplicado pela tela: null = filtro incompleto (mostra a dica);
 * um objeto novo = buscar de novo (página 1, seleção limpa, alertas reavaliados).
 */
@Component({
  selector: 'app-agenda-grade',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './agenda-grade.component.html',
  styleUrl: './agenda-grade.component.scss',
})
export class AgendaGradeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filtro: AgendaFiltroAplicado | null = null;
  @Input() contexto: AgendaContexto | null = null;
  @Input() dica = '';
  @Input() classificacoes: AgendaClassificacao[] = [];

  /** Horário livre escolhido (clique na linha, botão Agendar ou Novo agendamento). */
  @Output() novoAgendamento = new EventEmitter<AgendaGradeLinha>();
  @Output() encaixe = new EventEmitter<void>();
  @Output() acao = new EventEmitter<AgendaAcaoGradeEvento>();
  @Output() menuLinha = new EventEmitter<AgendaMenuLinhaEvento>();
  /** Rolagem lateral da grade: a tela fecha o menu aberto, que fica preso na posição antiga. */
  @Output() rolagemGrade = new EventEmitter<void>();
  @Output() selecaoMudou = new EventEmitter<AgendaGradeLinha[]>();
  @Output() bloqueioTotalMudou = new EventEmitter<boolean>();
  /** yyyy-MM-dd pedido pela própria grade (estado vazio); a tela troca a data do filtro. */
  @Output() irParaData = new EventEmitter<string>();

  readonly statusLegenda = STATUS_LEGENDA;
  readonly esperaPrincipal = esperaPrincipal;
  readonly esperaSub = esperaSub;
  readonly classeEspera = classeEspera;
  readonly classeStatus = classeStatus;
  readonly formatarCpf = formatarCpf;
  readonly subPaciente = subPaciente;
  readonly subAgendadoPor = subAgendadoPor;
  readonly ehLivre = ehLivre;
  readonly ehBloqueado = ehBloqueado;
  readonly ehCancelado = ehCancelado;
  readonly temPaciente = temPaciente;
  readonly selecionavel = selecionavel;
  readonly exibicaoConvenio = exibicaoConvenio;
  readonly descreverBloqueio = descreverBloqueio;

  linhas: AgendaGradeLinha[] = [];
  paginacao: AgendaPaginacao = { page: 1, limit: LIMITE_PAGINA, total: 0 };
  resumo: AgendaResumoDia | null = null;
  carregando = false;
  carregou = false;
  erroCarga = '';
  pagina = 1;
  ordenacao: AgendaOrdenacao = { ...ORDENACAO_PADRAO };
  legendaAberta = false;

  bloqueios: AgendaBloqueio[] = [];
  bloqueioTotal = false;
  verificandoBloqueio = false;
  turnos: AgendaAlertaTurno[] = [];
  private turnosDaAgenda: number | null = null;
  private fechados = new Set<string>();

  private selecao = new Set<string>();
  // Resposta de uma busca anterior chegando por último não pode sobrescrever a atual.
  private seqGrade = 0;
  private seqBloqueio = 0;
  private seqTurno = 0;
  private cargaEmVoo = false;

  colunasPrefs: AgendaColunaPref[] = colunasPadrao();
  /** O seletor só abre depois do GET, senão uma mudança feita antes seria sobrescrita por ele. */
  colunasProntas = false;
  seletorAberto = false;
  arrastando: string | null = null;
  // Sem o GET, gravar trocaria o layout salvo (também usado no sim-relatorios) pelo padrão.
  private colunasDoServidor = false;
  private timerColunas: ReturnType<typeof setTimeout> | null = null;
  erroColunas = '';
  private timerErroColunas: ReturnType<typeof setTimeout> | null = null;
  private destruido = false;
  private cacheColunas: { prefs: AgendaColunaPref[]; integrado: boolean; semMedico: boolean; lista: AgendaColunaDef[] } | null = null;

  constructor(
    private api: AgendaService,
    private modais: AgendaModaisService,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  // --- Transbordo lateral -------------------------------------------------------------------
  // Observa o card (janela redimensionada) e a tabela (carga de linhas, troca de colunas). A classe
  // vai direto no elemento: o callback roda fora da detecção de mudanças.
  private wrap: HTMLElement | null = null;
  private observador: ResizeObserver | null = null;

  @ViewChild('tabelaWrap') set tabelaWrap(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement ?? null;
    if (el === this.wrap) return;
    this.observador?.disconnect();
    this.observador = null;
    this.wrap = el;
    if (!el) return;
    if (typeof ResizeObserver !== 'undefined') {
      this.observador = new ResizeObserver(() => this.medirTransbordo());
      this.observador.observe(el);
      const tabela = el.querySelector('table');
      if (tabela) this.observador.observe(tabela);
    }
    this.medirTransbordo();
  }

  /** Grade mais larga que o card: só então as colunas presas ganham a divisória. */
  medirTransbordo(): boolean {
    const el = this.wrap;
    const transborda = !!el && el.scrollWidth > el.clientWidth;
    el?.classList.toggle('transborda', transborda);
    return transborda;
  }

  ngOnInit() {
    void this.carregarColunas();
  }

  ngOnChanges(ch: SimpleChanges) {
    if (ch['filtro']) this.aoMudarFiltro();
  }

  ngOnDestroy() {
    this.seqGrade++;
    this.seqBloqueio++;
    this.seqTurno++;
    this.destruido = true;
    this.observador?.disconnect();
    this.observador = null;
    this.wrap = null;
    if (this.timerErroColunas) clearTimeout(this.timerErroColunas);
    this.timerErroColunas = null;
    // Fechar o painel logo depois de mexer não pode perder a mudança.
    if (this.timerColunas) {
      clearTimeout(this.timerColunas);
      this.timerColunas = null;
      void this.salvarColunas();
    }
  }

  // --- Derivados ----------------------------------------------------------------------------

  get supervisor(): boolean { return !!this.contexto?.supervisor; }
  get integrado(): boolean { return this.filtro?.tipo === 'INTEGRADO'; }

  /** Entre o checkbox e o ⋮, na ordem e visibilidade da preferência. */
  get colunasGrade(): AgendaColunaDef[] {
    const semMedico = this.medicoOculto;
    const c = this.cacheColunas;
    if (c && c.prefs === this.colunasPrefs && c.integrado === this.integrado && c.semMedico === semMedico) return c.lista;
    let lista = colunasVisiveis(this.colunasPrefs, this.integrado);
    if (semMedico) lista = lista.filter(d => d.key !== 'medico');
    this.cacheColunas = { prefs: this.colunasPrefs, integrado: this.integrado, semMedico, lista };
    this.fixas = colunasFixas(lista);
    this.ultimaFixaCache = [...this.fixas.keys()].pop() ?? null;
    return lista;
  }

  // left (px) das colunas presas na rolagem lateral; recalculado junto com colunasGrade.
  private fixas = new Map<string, number>();
  private ultimaFixaCache: string | null = null;

  get ultimaFixa(): string | null {
    void this.colunasGrade;
    return this.ultimaFixaCache;
  }

  esquerda(key: string): number | null {
    void this.colunasGrade;
    return this.fixas.get(key) ?? null;
  }

  get colunas(): number { return this.colunasGrade.length + 2; }

  get itensColunas() {
    const oculto = this.medicoOculto;
    return itensSeletor(this.colunasPrefs).map(it => (oculto && it.def.key === 'medico' ? { ...it, visivel: false } : it));
  }

  /**
   * Nome do médico quando a grade é de uma agenda só e todas as linhas são dele; '' = agenda sem
   * médico nas linhas. null = Integrado, sem agenda ou médicos diferentes (agenda de exame).
   */
  get medicoUnico(): string | null {
    if (!this.filtro?.cdAgenda || this.integrado) return null;
    const nomes = new Set<string>();
    for (const l of this.linhas) {
      const n = (l.medico?.nmMedico || l.medicoAgenda?.nmMedico || '').trim();
      if (n) nomes.add(n);
      if (nomes.size > 1) return null;
    }
    return nomes.size ? [...nomes][0] : '';
  }

  /** Médico repetido em toda linha some, salvo se o usuário o ligou à mão no seletor. */
  get medicoOculto(): boolean {
    if (this.medicoUnico === null) return false;
    return !this.colunasPrefs.find(p => p.key === 'medico')?.fixo;
  }

  /** O médico some da grade; aparece uma vez no resumo se a agenda não traz o nome. */
  get medicoNoResumo(): string {
    const nome = this.medicoOculto ? this.medicoUnico : '';
    if (!nome) return '';
    const ds = (this.filtro?.agenda?.dsAgenda ?? '').toLocaleLowerCase('pt-BR');
    return ds.includes(nome.toLocaleLowerCase('pt-BR')) ? '' : nome;
  }


  get chips(): AgendaChipResumo[] {
    if (!this.resumo || !this.filtro) return [];
    return chipsResumo(this.resumo, this.linhas, this.filtro.tipo);
  }

  get legenda(): LegendaClassificacao[] {
    return legendaClassificacoes(this.linhas, this.classificacoes, this.filtro?.tipo);
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.paginacao.total / (this.paginacao.limit || LIMITE_PAGINA)));
  }

  get faixa(): string {
    const { page, limit, total } = this.paginacao;
    if (!total) return '0 de 0';
    return `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} de ${total}`;
  }

  /** Dia sem nenhum horário gerado (não é filtro escondendo linha). */
  get diaSemHorario(): boolean {
    return this.carregou && !this.linhas.length && !this.paginacao.total && !this.resumo?.horariosDia;
  }

  get observacao(): string | null {
    const ag = this.filtro?.agenda;
    const obs = ag?.dsObservacao?.trim();
    return obs && !this.fechados.has(`obs-${ag!.cdAgenda}`) ? obs : null;
  }

  get turnosVisiveis(): AgendaAlertaTurno[] {
    return this.turnosDaAgenda !== null && !this.fechados.has(`turno-${this.turnosDaAgenda}`) ? this.turnos : [];
  }

  get severidadeTurno(): 'bad' | 'warn' | 'info' {
    if (this.turnos.some(t => t.severidade === 'alta')) return 'bad';
    return this.turnos.some(t => t.severidade === 'media') ? 'warn' : 'info';
  }

  corClassif(l: AgendaGradeLinha): string {
    return corClassificacao(l.dsClassifAgenda, classificacoesDoTipo(this.classificacoes, l.tipo));
  }

  rotuloEspera(l: AgendaGradeLinha): string {
    const t = formatarEspera(l.qtMinEspera ?? 0);
    return l.dtFimAtendimento ? `Esperou ${t}, da chegada ao fim do atendimento` : `Aguardando há ${t}, desde a abertura do atendimento`;
  }

  // --- Seleção ------------------------------------------------------------------------------

  get selecionadas(): AgendaGradeLinha[] {
    return this.linhas.filter(l => this.selecao.has(l.id));
  }

  get marcadas(): AgendaGradeLinha[] {
    return this.selecionadas.filter(ehMarcado);
  }

  estaSelecionada(l: AgendaGradeLinha): boolean {
    return this.selecao.has(l.id);
  }

  private get marcaveisDaPagina(): AgendaGradeLinha[] {
    return this.linhas.filter(l => selecionavel(l) && ehMarcado(l));
  }

  get todasMarcadas(): boolean {
    const m = this.marcaveisDaPagina;
    return m.length > 0 && m.every(l => this.selecao.has(l.id));
  }

  alternar(l: AgendaGradeLinha, marcar?: boolean) {
    if (!selecionavel(l)) return;
    const vai = marcar ?? !this.selecao.has(l.id);
    if (vai) this.selecao.add(l.id); else this.selecao.delete(l.id);
    this.emitirSelecao();
  }

  /**
   * Abrir o menu (⋮ ou botão direito) sobre uma linha fora da seleção troca a seleção por só ela;
   * sobre uma linha já marcada, mantém a multi-seleção para a ação em lote.
   */
  private selecaoParaMenu(l: AgendaGradeLinha) {
    if (this.selecao.has(l.id)) return;
    this.selecao.clear();
    if (selecionavel(l)) this.selecao.add(l.id);
    this.emitirSelecao();
  }

  /** O "todos" do cabeçalho pega só os agendados da página. */
  marcarTodas(marcar: boolean) {
    for (const l of this.marcaveisDaPagina) {
      if (marcar) this.selecao.add(l.id); else this.selecao.delete(l.id);
    }
    this.emitirSelecao();
  }

  limparSelecao() {
    if (!this.selecao.size) return;
    this.selecao.clear();
    this.emitirSelecao();
  }

  private emitirSelecao() {
    this.selecaoMudou.emit(this.selecionadas);
  }

  clicarLinha(l: AgendaGradeLinha) {
    if (!selecionavel(l)) return;
    if (ehLivre(l) && !ehBloqueado(l)) {
      this.agendar(l);
      return;
    }
    this.alternar(l);
  }

  // --- Ações (preenchidas pelos modais da tela) ---------------------------------------------

  /** Novo agendamento: um horário livre selecionado, sem bloqueio total no dia. */
  get podeNovo(): boolean {
    const sel = this.selecionadas;
    return this.liberaNovo && sel.length === 1 && aceitaAgendamento(sel[0], this.supervisor);
  }

  get motivoSemNovo(): string {
    if (this.bloqueioTotal) return 'Bloqueio total da agenda nesta data';
    if (this.verificandoBloqueio) return 'Verificando bloqueios da agenda...';
    return this.podeNovo ? 'Agendar no horário selecionado' : 'Selecione um horário livre';
  }

  /** Bloqueio total (ou ainda sem saber) trava qualquer agendamento novo. */
  get liberaNovo(): boolean {
    return !this.bloqueioTotal && !this.verificandoBloqueio;
  }

  get motivoEncaixe(): string {
    if (this.bloqueioTotal) return 'Bloqueio total da agenda nesta data';
    if (this.verificandoBloqueio) return 'Verificando bloqueios da agenda...';
    if (!this.supervisor && !this.contexto?.encaixeLivre) return 'Somente supervisão';
    return this.filtro?.cdAgenda ? 'Horário extra fora da grade' : 'Escolha uma agenda para encaixar';
  }

  /** O encaixe entra numa agenda: no Integrado (só médico) não há onde inserir. */
  get podeEncaixe(): boolean {
    return !!this.filtro?.cdAgenda && this.filtro.tipo !== 'INTEGRADO' && this.liberaNovo
      && (this.supervisor || !!this.contexto?.encaixeLivre);
  }

  get reservaAlvo(): AgendaGradeLinha | null {
    const sel = this.selecionadas;
    if (!this.supervisor || sel.length !== 1 || temPaciente(sel[0])) return null;
    return ['L', 'R'].includes(sel[0].ieStatusAgenda ?? '') && !ehBloqueado(sel[0]) ? sel[0] : null;
  }

  agendar(l: AgendaGradeLinha) {
    if (!this.liberaNovo || !aceitaAgendamento(l, this.supervisor)) return;
    this.novoAgendamento.emit(l);
  }

  novo() {
    if (this.podeNovo) this.novoAgendamento.emit(this.selecionadas[0]);
  }

  pedirEncaixe() {
    if (this.podeEncaixe) this.encaixe.emit();
  }

  reservarOuLiberar() {
    const alvo = this.reservaAlvo;
    if (!alvo) return;
    this.acao.emit({ acao: alvo.ieStatusAgenda === 'R' ? 'liberarReserva' : 'reservar', linhas: [alvo] });
  }

  /** Motivo de a ação da barra estar desabilitada para a seleção atual; null = habilitada. */
  motivoAcao(acao: AgendaAcaoAgendamento): string | null {
    return motivoAcaoSelecao(acao, this.selecionadas);
  }

  pedirAcao(acao: AgendaAcaoAgendamento) {
    if (this.motivoAcao(acao)) return;
    this.acao.emit({ acao, linhas: this.marcadas });
  }

  abrirMenu(l: AgendaGradeLinha, ev: Event) {
    ev.stopPropagation();
    this.selecaoParaMenu(l);
    this.menuLinha.emit({
      linha: l, alvo: ev.currentTarget as HTMLElement,
      marcadas: this.marcadas, podeEncaixe: this.podeEncaixe, motivoEncaixe: this.motivoEncaixe,
    });
  }

  /**
   * Botão direito na linha: mesmo menu combinado do ⋮, na posição do cursor. Fora de uma linha
   * acionável (cancelada, bloqueada sem paciente) segue o menu padrão do navegador.
   */
  aoContextMenu(l: AgendaGradeLinha, ev: MouseEvent) {
    if (!selecionavel(l)) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.selecaoParaMenu(l);
    this.menuLinha.emit({
      linha: l, ponto: { x: ev.clientX, y: ev.clientY },
      marcadas: this.marcadas, podeEncaixe: this.podeEncaixe, motivoEncaixe: this.motivoEncaixe,
    });
  }

  fecharAlerta(chave: string) {
    this.fechados.add(chave);
  }

  diaVizinho(n: number) {
    if (this.filtro) this.irParaData.emit(somarDias(this.filtro.data, n));
  }

  // --- Ordenação e página -------------------------------------------------------------------

  ordenar(campo: string) {
    this.ordenacao = this.ordenacao.campo === campo
      ? { campo, ordem: this.ordenacao.ordem === 'asc' ? 'desc' : 'asc' }
      : { campo, ordem: 'asc' };
    this.pagina = 1;
    void this.carregar({ piscar: true, manterSelecao: false, mostrarErro: true });
  }

  indicador(campo: string): string {
    if (this.ordenacao.campo !== campo) return '';
    return this.ordenacao.ordem === 'asc' ? ' ▲' : ' ▼';
  }

  irPagina(n: number) {
    if (n < 1 || n > this.totalPaginas || n === this.pagina) return;
    this.pagina = n;
    void this.carregar({ piscar: true, manterSelecao: false, mostrarErro: true });
  }

  // --- Cargas -------------------------------------------------------------------------------

  /** Para os modais depois de gravar, e para o botão ↻: busca de novo sem piscar. */
  recarregar(manterSelecao = false): Promise<void> {
    return this.carregar({ piscar: false, manterSelecao, mostrarErro: true });
  }

  private aoMudarFiltro() {
    const f = this.filtro;
    this.pagina = 1;
    this.selecao.clear();
    if (!f) {
      this.seqGrade++;
      this.seqBloqueio++;
      this.seqTurno++;
      this.linhas = [];
      this.resumo = null;
      this.paginacao = { page: 1, limit: LIMITE_PAGINA, total: 0 };
      this.carregou = false;
      this.carregando = false;
      this.cargaEmVoo = false;
      this.erroCarga = '';
      this.bloqueios = [];
      this.turnos = [];
      this.turnosDaAgenda = null;
      this.verificandoBloqueio = false;
      this.definirBloqueioTotal(false);
      this.emitirSelecao();
      return;
    }
    // Carga disparada por troca de filtro só troca o dado quando chega, sem esqueleto.
    void this.carregar({ piscar: !(f.automatico && this.carregou), manterSelecao: false, mostrarErro: true });
    void this.verificarBloqueio(f);
    void this.verificarTurno(f);
  }

  private async carregar(o: OpcoesCarga): Promise<void> {
    const f = this.filtro;
    if (!f) return;
    const seq = ++this.seqGrade;
    this.cargaEmVoo = true;
    if (o.piscar) this.carregando = true;
    try {
      const g = await this.api.grade({
        ...paramsGrade(f),
        page: this.pagina,
        limit: LIMITE_PAGINA,
        sort: this.ordenacao.campo,
        order: this.ordenacao.ordem,
      });
      if (seq !== this.seqGrade) return;
      this.linhas = g.linhas;
      this.paginacao = g.paginacao;
      this.resumo = g.resumo;
      this.carregou = true;
      this.erroCarga = '';
      const antes = this.selecao.size;
      const ids = new Set(g.linhas.filter(selecionavel).map(l => l.id));
      if (o.manterSelecao) this.selecao = new Set([...this.selecao].filter(id => ids.has(id)));
      else this.selecao.clear();
      if (antes || this.selecao.size) this.emitirSelecao();
    } catch (e) {
      if (seq !== this.seqGrade) return;
      if (o.mostrarErro) {
        this.erroCarga = mensagemErroAgenda(e, 'Não foi possível carregar a agenda.');
        this.linhas = [];
        this.resumo = null;
        this.paginacao = { page: 1, limit: LIMITE_PAGINA, total: 0 };
        this.carregou = false;
        if (this.selecao.size) { this.selecao.clear(); this.emitirSelecao(); }
      }
    } finally {
      if (seq === this.seqGrade) {
        this.carregando = false;
        this.cargaEmVoo = false;
      }
    }
  }

  tentarDeNovo() {
    void this.carregar({ piscar: true, manterSelecao: false, mostrarErro: true });
  }

  private async verificarBloqueio(f: AgendaFiltroAplicado) {
    const seq = ++this.seqBloqueio;
    this.bloqueios = [];
    this.definirBloqueioTotal(false);
    if (!f.cdAgenda) {
      this.verificandoBloqueio = false;
      return;
    }
    this.verificandoBloqueio = true;
    try {
      const lista = await this.api.bloqueios(f.cdAgenda, f.data);
      if (seq !== this.seqBloqueio) return;
      this.bloqueios = lista;
      this.definirBloqueioTotal(bloqueioTotal(lista));
    } catch {
      // Sem a resposta não há o que avisar; a gravação revalida o bloqueio no servidor.
    } finally {
      if (seq === this.seqBloqueio) this.verificandoBloqueio = false;
    }
  }

  private definirBloqueioTotal(v: boolean) {
    if (this.bloqueioTotal === v) return;
    this.bloqueioTotal = v;
    this.bloqueioTotalMudou.emit(v);
  }

  private async verificarTurno(f: AgendaFiltroAplicado) {
    const seq = ++this.seqTurno;
    if (f.cdAgenda !== this.turnosDaAgenda) {
      this.turnos = [];
      this.turnosDaAgenda = null;
    }
    if (!f.cdAgenda) return;
    const chave = chaveTurno(f.cdAgenda, f.cdEstabelecimento);
    if (this.jaAvisado(chave)) return;
    try {
      const lista = await this.api.turnoEncerramento({
        cdAgenda: f.cdAgenda, cdEstabelecimento: f.cdEstabelecimento, dias: DIAS_AVISO_TURNO,
      });
      if (seq !== this.seqTurno) return;
      if (!lista.length) return;
      this.turnos = lista;
      this.turnosDaAgenda = f.cdAgenda;
      this.marcarAvisado(chave);
    } catch {
      // Aviso informativo: falha não atrapalha a grade.
    }
  }

  // sessionStorage pode faltar (janela privada, bloqueio de site): sem ele o aviso só repete.
  private jaAvisado(chave: string): boolean {
    try {
      return !!this.doc?.defaultView?.sessionStorage.getItem(chave);
    } catch {
      return false;
    }
  }

  private marcarAvisado(chave: string) {
    try {
      this.doc?.defaultView?.sessionStorage.setItem(chave, '1');
    } catch {
      /* segue sem lembrar */
    }
  }

  // --- Colunas ------------------------------------------------------------------------------

  @HostListener('document:keydown.escape')
  fecharSeletor() {
    if (this.modais.coberto()) return;
    this.seletorAberto = false;
  }

  private async carregarColunas() {
    try {
      this.colunasPrefs = mesclarColunas(await this.api.preferenciasColunas());
      this.colunasDoServidor = true;
    } catch {
      // Sem a preferência a grade segue no padrão.
    } finally {
      this.colunasProntas = true;
    }
  }

  alternarColuna(key: string) {
    if (key === 'medico') {
      const naGrade = this.colunasGrade.some(c => c.key === 'medico');
      this.mudarColunas(definirMedico(this.colunasPrefs, !naGrade));
      return;
    }
    this.mudarColunas(alternarColuna(this.colunasPrefs, key));
  }

  moverColuna(key: string, delta: -1 | 1) {
    this.mudarColunas(moverColuna(this.colunasPrefs, key, delta));
  }

  restaurarColunas() {
    this.mudarColunas(restaurarColunas(this.colunasPrefs));
  }

  iniciarArraste(key: string, ev: DragEvent) {
    this.arrastando = key;
    ev.dataTransfer?.setData('text/plain', key);
  }

  soltarEm(alvo: string, ev: Event) {
    ev.preventDefault();
    const key = this.arrastando;
    this.arrastando = null;
    if (key) this.mudarColunas(moverColunaPara(this.colunasPrefs, key, alvo));
  }

  private mudarColunas(prefs: AgendaColunaPref[]) {
    if (prefs === this.colunasPrefs) return;
    this.colunasPrefs = prefs;
    if (!this.colunasDoServidor) return;
    if (this.timerColunas) clearTimeout(this.timerColunas);
    this.timerColunas = setTimeout(() => {
      this.timerColunas = null;
      void this.salvarColunas();
    }, ESPERA_SALVAR_COLUNAS_MS);
  }

  /** Aviso sem bloquear: a tela segue com a escolha, mas ela não vai valer no próximo acesso. */
  private async salvarColunas() {
    try {
      await this.api.salvarPreferenciasColunas(this.colunasPrefs);
      this.avisarColunas('');
    } catch {
      this.avisarColunas('Não foi possível salvar as colunas.');
    }
  }

  private avisarColunas(msg: string) {
    if (this.destruido) return;
    if (this.timerErroColunas) clearTimeout(this.timerErroColunas);
    this.timerErroColunas = null;
    this.erroColunas = msg;
    if (msg) this.timerErroColunas = setTimeout(() => { this.erroColunas = ''; this.timerErroColunas = null; }, DURACAO_ERRO_COLUNAS_MS);
  }

  trackColuna(_: number, c: AgendaColunaDef): string {
    return c.key;
  }

  trackItemColuna(_: number, it: { def: AgendaColunaDef }): string {
    return it.def.key;
  }

  formatarData(iso: string | null): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso ?? '';
  }

  trackLinha(_: number, l: AgendaGradeLinha): string {
    return l.id;
  }
}
